import HttpsRoundedIcon from '@mui/icons-material/HttpsRounded';
import LockResetRoundedIcon from '@mui/icons-material/LockResetRounded';
import { LoadingButton } from '@mui/lab';
import { Alert, Typography, useTheme } from '@mui/material';
import { passwordStrength } from 'check-password-strength';
import { useFormik } from 'formik';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { object, ObjectSchema, ref, string } from 'yup';
import AuthApi from '../../../apis/AuthApi';
import FormikTextField from '../../../components/FormikTextField/FormikTextField';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { ResetPasswordDto } from '../../../generated-types/resetPasswordDto';
import useApi from '../../../lib/useApi';

/**
 * A dto that's modified from what the backend expects. This is used explicitly with formik.
 */
export interface ResetPasswordWithConfirmDto extends ResetPasswordDto {
  confirmPassword: string;
}

/**
 * The reset password form props
 */
interface ResetPasswordFormProps {
  /**
   * The email to reset the password for
   */
  email: string;
  /**
   * The forgot password code to reset with
   */
  code: string;
  /**
   * Set that the password was successfully reset
   */
  setSuccessfulReset: () => void;
}

/**
 * A form to reset the password for the user
 */
const ResetPasswordForm = (props: ResetPasswordFormProps) => {
  const authApi = useApi(AuthApi);
  const theme = useTheme();
  const navigate = useNavigate();
  const snackbarContext = useContext(SnackbarContext);
  const schema: ObjectSchema<ResetPasswordWithConfirmDto> = object({
    email: string().defined('Required'),
    code: string().defined('Required'),
    newPassword: string()
      .defined('Required')
      .test(
        'password-strength',
        'Password must contain a lowercase letter, uppercase letter, number, special symbol, and be at least 8 characters long.',
        (password) => passwordStrength(password).id >= 2
      ),
    confirmPassword: string()
      .defined('Required')
      .oneOf([ref('newPassword')], 'Passwords do not match'),
  });

  const formik = useFormik<ResetPasswordWithConfirmDto>({
    initialValues: {
      email: props.email,
      code: props.code,
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: schema,
    onSubmit: (val, { setSubmitting }) => {
      authApi
        .ResetPassword(val)
        .then((successfulReset) => {
          if (successfulReset) {
            props.setSuccessfulReset();
          } else {
            snackbarContext.setAlert(
              <Alert severity="error" variant="filled">
                The password reset has expired. Please initiate a new forgot
                password.
              </Alert>
            );
            navigate('/login');
            snackbarContext.triggerSnackbar();
          }
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert severity="error" variant="filled">
              An error occurred while resetting your password. If this
              continues, please contact us.
            </Alert>
          );
          snackbarContext.triggerSnackbar();
        })
        .finally(() => setSubmitting(false));
    },
  });

  return (
    <>
      <LockResetRoundedIcon sx={{ fontSize: '4em', marginY: '.25em' }} />
      <Typography
        variant="h1"
        fontSize="1.5em"
        fontWeight="bold"
        gutterBottom
        textAlign="center"
      >
        Reset password
      </Typography>
      <Typography
        color={theme.palette.text.secondary}
        textAlign="center"
        sx={{ maxWidth: '90%', marginBottom: '2em' }}
      >
        To reset your password, complete the form below.
      </Typography>
      <form onSubmit={formik.handleSubmit}>
        <FormikTextField
          formik={formik}
          fullWidth
          label="New Password"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          sx={{
            marginBottom: '1em',
          }}
        />
        <FormikTextField
          formik={formik}
          fullWidth
          label="Confirm New Password"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          sx={{
            marginBottom: '1em',
          }}
        />
        <LoadingButton
          loading={formik.isSubmitting}
          loadingPosition="end"
          type="submit"
          variant="contained"
          endIcon={<HttpsRoundedIcon />}
          fullWidth
        >
          Reset Password
        </LoadingButton>
      </form>
    </>
  );
};

export default ResetPasswordForm;
